import React, {createContext, useCallback, useContext, useMemo, useRef} from 'react';
import { cloneDeep } from 'lodash';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

const DataContext = createContext(null)

function createDataStore(initialData, surveyId) {
    let data = initialData
    let history = []
    let future = []
    const maxHistorySize = 50
    const keyListeners = new Map()
    const undoRedoListeners = new Set()

    const trackedKeys = ['surveyData', 'userSurveyData']

    let historyPushPending = false
    let undoRedoSnapshot = { historyLength: 0, futureLength: 0 }

    function subscribe(key, callback) {
        if (!keyListeners.has(key)) keyListeners.set(key, new Set())
        keyListeners.get(key).add(callback)
        return () => keyListeners.get(key).delete(callback)
    }

    function subscribeUndoRedo(callback) {
        undoRedoListeners.add(callback)
        return () => undoRedoListeners.delete(callback)
    }

    function getSnapshot(key) {
        return data[key]
    }

    function getHistory() { return history }
    function getFuture() { return future }

    function notifyKey(key) {
        keyListeners.get(key)?.forEach(cb => cb())
    }

    function getUndoRedoSnapshot() {
        return undoRedoSnapshot
    }

    function notifyUndoRedo() {
        undoRedoSnapshot = { historyLength: history.length, futureLength: future.length }
        undoRedoListeners.forEach(cb => cb())
    }

    function setKey(key, value, updateHistory = true) {
        if (updateHistory && trackedKeys.includes(key) && !historyPushPending) {
            const snapshot = {}
            trackedKeys.forEach(k => { snapshot[k] = data[k] })
            history = [...history, snapshot].slice(-maxHistorySize)
            future = []
            historyPushPending = true
            queueMicrotask(() => { historyPushPending = false })
            notifyUndoRedo()
        }

        if ((key === 'surveyData' || key === 'userSurveyData') && surveyId) {
            sessionStorage.setItem(`${key}_${surveyId}`, JSON.stringify(value))
        }

        data = { ...data, [key]: value }
        notifyKey(key)
    }

    function undo() {
        if (history.length === 0) return
        const snapshot = history[history.length - 1]
        history = history.slice(0, -1)

        const currentSnapshot = {}
        trackedKeys.forEach(k => { currentSnapshot[k] = data[k] })
        future = [currentSnapshot, ...future]

        const oldData = data
        data = { ...data }
        trackedKeys.forEach(k => {
            data[k] = snapshot[k]
        })

        trackedKeys.forEach(k => {
            if (oldData[k] !== data[k]) notifyKey(k)
        })

        trackedKeys.forEach(k => {
            if (surveyId) {
                sessionStorage.setItem(`${k}_${surveyId}`, JSON.stringify(data[k]))
            }
        })

        notifyUndoRedo()
    }

    function redo() {
        if (future.length === 0) return
        const snapshot = future[0]
        future = future.slice(1)

        const currentSnapshot = {}
        trackedKeys.forEach(k => { currentSnapshot[k] = data[k] })
        history = [...history, currentSnapshot]

        const oldData = data
        data = { ...data }
        trackedKeys.forEach(k => {
            data[k] = snapshot[k]
        })

        trackedKeys.forEach(k => {
            if (oldData[k] !== data[k]) notifyKey(k)
        })

        trackedKeys.forEach(k => {
            if (surveyId) {
                sessionStorage.setItem(`${k}_${surveyId}`, JSON.stringify(data[k]))
            }
        })

        notifyUndoRedo()
    }

    return {
        subscribe, subscribeUndoRedo,
        getSnapshot, getUndoRedoSnapshot,
        setKey, undo, redo
    }
}

export const useDataContext = (key) => {
    const store = useContext(DataContext)
    if (!store) {
        throw new Error('useDataContext must be used within a DataProvider')
    }

    const subscribe = useCallback(
        (callback) => store.subscribe(key, callback),
        [store, key]
    )
    const getSnapshot = useCallback(
        () => store.getSnapshot(key),
        [store, key]
    )

    const rawValue = useSyncExternalStore(subscribe, getSnapshot)
    const value = useMemo(() => cloneDeep(rawValue), [rawValue])

    const setValue = useCallback(
        (newValue, updateHistory = true) => store.setKey(key, newValue, updateHistory),
        [store, key]
    )

    return { value, setValue }
}

export const useDataContextReadOnly = (key) => {
    const store = useContext(DataContext)
    if (!store) {
        throw new Error('useDataContextReadOnly must be used within a DataProvider')
    }

    const subscribe = useCallback(
        (callback) => store.subscribe(key, callback),
        [store, key]
    )
    const getSnapshot = useCallback(
        () => store.getSnapshot(key),
        [store, key]
    )

    const value = useSyncExternalStore(subscribe, getSnapshot)

    const setValue = useCallback(
        (newValue, updateHistory = true) => store.setKey(key, newValue, updateHistory),
        [store, key]
    )

    return { value, setValue }
}

export const useUndoRedo = () => {
    const store = useContext(DataContext)
    if (!store) {
        throw new Error('useUndoRedo must be used within a DataProvider')
    }

    const subscribe = useCallback(
        (callback) => store.subscribeUndoRedo(callback),
        [store]
    )
    const getSnapshot = useCallback(
        () => store.getUndoRedoSnapshot(),
        [store]
    )

    const { historyLength, futureLength } = useSyncExternalStore(subscribe, getSnapshot)

    return {
        undo: store.undo,
        redo: store.redo,
        canUndo: historyLength > 0,
        canRedo: futureLength > 0,
    }
}

export const DataProvider = ({ children, surveyId }) => {
    const storeRef = useRef(null)
    if (!storeRef.current) {
        const initialState = {}
        if (surveyId) {
            const storedSurveyData = sessionStorage.getItem(`surveyData_${surveyId}`)
            const storedUserSurveyData = sessionStorage.getItem(`userSurveyData_${surveyId}`)
            initialState.surveyData = storedSurveyData ? JSON.parse(storedSurveyData) : null
            initialState.userSurveyData = storedUserSurveyData ? JSON.parse(storedUserSurveyData) : null
        }
        storeRef.current = createDataStore(initialState, surveyId)
    }

    return (
        <DataContext.Provider value={storeRef.current}>{children}</DataContext.Provider>
    )
}

export const CollapsedViewContext = React.createContext(null)

export const CollapsedViewProvider = ({ children }) => {
    const [isCollapsed, setIsCollapsed] = React.useState(false);

    return (
        <CollapsedViewContext.Provider value={{ isCollapsed, setIsCollapsed }}>
            {children}
        </CollapsedViewContext.Provider>
    );
};
